import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { REPORT_TYPE_REGISTRY } from "./reportTypeRegistry";
import "./ReportsPage.css";

// `titleKey` arrives as plain `string` on `ReportTypeMeta` (Task 12), not a
// compile-time literal union, so `t()` can't type-check it against the
// generated key union — same situation ReportSummaryRow.tsx's own
// `translateDynamic` already solved: cast `as never` and supply the raw
// key as `defaultValue` so a genuinely missing key still renders something
// instead of throwing.
function translateDynamic(t: TFunction<"reports">, key: string, fallback: string): string {
  return t(key as never, { defaultValue: fallback });
}

/**
 * NormaX Performance Intelligence — Reports landing page (R1 Task 14,
 * premium-presentation-polish pass).
 *
 * Deliberately omits a "Recent Reports" section — `ReportRun` doesn't exist
 * yet (R1 is read-only report-view infrastructure only), so a recent-reports
 * list would be fake. The "Automatic Reports" block below is a static
 * teaser: no button, no scheduler in R1.
 */
export function ReportsPage() {
  const { t } = useTranslation("reports");

  return (
    <div className="nx-reports-landing">
      <header className="nx-reports-landing__header">
        <h1 className="nx-reports-landing__title">{t("landing.title")}</h1>
        <p className="nx-reports-landing__subtitle">{t("landing.subtitle")}</p>
      </header>

      <section aria-labelledby="nx-reports-quick-heading">
        <h2 id="nx-reports-quick-heading" className="nx-reports-landing__section-title">
          {t("landing.quickReportsHeading")}
        </h2>
        <ul className="nx-reports-landing__grid" role="list">
          {REPORT_TYPE_REGISTRY.map((meta) => {
            const Icon = meta.icon;
            return (
              <li key={meta.reportType}>
                <Link className="nx-reports-landing__card" to={`/izvestaji?type=${meta.reportType}`}>
                  <span className="nx-reports-landing__card-icon" aria-hidden="true">
                    <Icon size={20} aria-hidden="true" />
                  </span>
                  <span className="nx-reports-landing__card-body">
                    <span className="nx-reports-landing__card-title">{translateDynamic(t, meta.titleKey, meta.titleKey)}</span>
                    <span className="nx-reports-landing__card-descriptor">{translateDynamic(t, meta.descriptorKey, "")}</span>
                  </span>
                  <ArrowRight className="nx-reports-landing__card-arrow" size={16} aria-hidden="true" />
                </Link>
              </li>
            );
          })}
        </ul>
      </section>

      <section className="nx-reports-landing__automatic" aria-labelledby="nx-reports-automatic-heading">
        <h2 id="nx-reports-automatic-heading" className="nx-reports-landing__section-title">
          {t("landing.automaticReportsTitle")}
        </h2>
        <p className="nx-reports-landing__automatic-body">{t("landing.automaticReportsBody")}</p>
      </section>
    </div>
  );
}
